// packages/console/src/components/panels/OutputProfilePanel.tsx
//
// Output profile (right column): a read-only LIST of the active preset's real
// FT-3 settings. Editing moved to the center flyout (PresetEditorModal) so the
// right column stays compact and nothing scrolls. The "Edit" affordance opens
// the flyout for the active preset. When EVE is on the pane shows the EVE-derived
// rows and a lock badge (AM-11). Derived rows are absent (em dash) when an input
// is unknown; the prototype's magic-number estimator is not ported (R3, R10(b)).

import { useEffect, useState } from "react";
import { Panel, Row } from "../Parts";
import { Icon } from "../Icons";
import { EM_DASH } from "../../lib/format";
import { SPEED_CHIPS, codecLabel, estOutputLabel, keyframeLabel, resolutionLabel, scalingLabel, speedChipFor } from "../../lib/preset";
import type { Job, Preset, PresetCommand } from "../../api/jobs";
import { getPresetCommand, patchPreset } from "../../api/jobs";
import type { EveState } from "./EvePanel";

// keyframeSummary reads the GOP frames row and appends the forced-keyframe
// cadence in seconds when one is set (0 = frames only).
function keyframeSummary(p: Preset): string {
  const base = keyframeLabel(p);
  return p.keyframeSeconds && p.keyframeSeconds > 0 ? `${base} · ${p.keyframeSeconds}s` : base;
}

// segmentSummary reads the HLS/DASH segment target (default 6 when unset).
function segmentSummary(p: Preset): string {
  return `${p.segmentSeconds && p.segmentSeconds > 0 ? p.segmentSeconds : 6}s`;
}

// pictureSummary reads the preset-global scaling + bit depth honestly, flagging
// 10-bit as web-unsafe (matching the flyout's warning).
function pictureSummary(p: Preset): string {
  const depth = p.bitDepth === 10 ? "10-bit (web-unsafe)" : "8-bit";
  return `${scalingLabel(p.scaling)} · ${depth}`;
}

function rateSummary(p: Preset): string {
  if (p.rateControl === "crf") return `CRF ${p.crf ?? EM_DASH}`;
  const mbps = ((p.bitrateKbps ?? 0) / 1000).toFixed(1);
  const max = p.maxBitrateKbps && p.maxBitrateKbps > 0 ? ` (max ${(p.maxBitrateKbps / 1000).toFixed(1)})` : "";
  return `${p.rateControl.toUpperCase()} ${mbps} Mb/s${max}`;
}

function speedLabel(p: Preset): string {
  const id = speedChipFor(p.speedPreset);
  return SPEED_CHIPS.find((s) => s.id === id)?.label ?? p.speedPreset;
}

function audioSummary(p: Preset): string {
  if (p.audioDisabled) return "Off";
  const ch = p.audioChannels === 1 ? "mono" : p.audioChannels === 2 ? "stereo" : "source";
  if (p.perRungAudio) return `Per rung · ${ch}`;
  const kbps = p.audioBitrateKbps && p.audioBitrateKbps > 0 ? p.audioBitrateKbps : 128;
  return `${kbps} kb/s · ${ch}`;
}

// codecSummary shows the default codec, noting when rungs override it per rung.
function codecSummary(p: Preset): string {
  const base = codecLabel(p.videoCodec);
  const ladder = p.ladder ?? [];
  return ladder.some((r) => r.videoCodec && r.videoCodec !== p.videoCodec) ? `${base} · per rung` : base;
}

function formatsSummary(p: Preset): string {
  const fs = p.formats && p.formats.length ? p.formats : [p.container];
  return fs.map((f) => f.toUpperCase()).join(" · ");
}

function ladderSummary(p: Preset): string {
  const ladder = p.ladder ?? [];
  if (ladder.length === 0) return EM_DASH;
  return [...ladder]
    .sort((a, b) => a.height - b.height)
    .map((r) => r.name || `${r.height}p`)
    .join(" · ");
}

// EVE_RUNG_SET is the EVE ABR ladder (mirrors jobs.EveLadder on the server):
// EVE profiles may only carry rungs from this set, and the restricted editor
// limits resolution selection to exactly these.
const EVE_RUNG_SET = ["2160p", "1080p", "720p", "540p", "360p"];

// commandText renders a per-rung generated argv as a single readable line: it
// space-joins the argv, quoting any element that carries a space so the copy is
// unambiguous. This is a DISPLAY seed only; the server tokenizes the edited
// string back into argv (shell-word split, no shell).
function commandText(argv: string[]): string {
  return argv.map((a) => (/\s/.test(a) ? JSON.stringify(a) : a)).join(" ");
}

// EveFullView is the Phase 3 admin editable-command surface (owners only). It
// shows the exact ffmpeg command the engine would run for each rung, then lets
// an owner edit a single command override and Save it. The override is ARGV
// (the server shell-word-splits it, no shell), so the panel says so plainly.
// The textarea is seeded with the current cmdOverride, or the FIRST rung's
// generated command when no override is set yet (the real starting point).
function EveFullView({
  preset,
  onBack,
  onReload,
}: {
  preset: Preset;
  onBack: () => void;
  onReload: () => void;
}) {
  const [cmd, setCmd] = useState<PresetCommand | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [draft, setDraft] = useState<string>(preset.cmdOverride ?? "");
  const [seeded, setSeeded] = useState<boolean>(Boolean(preset.cmdOverride));
  const [busy, setBusy] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const ac = new AbortController();
    getPresetCommand(preset.id, ac.signal)
      .then((c) => {
        setCmd(c);
        // Seed the editor from the generated first-rung command only when the
        // preset has no override yet, so an owner starts from the real command.
        if (!seeded && !c.cmdOverride) {
          if (c.rungs.length > 0) setDraft(commandText(c.rungs[0].argv));
          setSeeded(true);
        } else if (!seeded && c.cmdOverride) {
          setDraft(c.cmdOverride);
          setSeeded(true);
        }
      })
      .catch((e) => {
        if (ac.signal.aborted) return;
        setLoadErr(e instanceof Error ? e.message : "failed to load command");
      });
    return () => ac.abort();
    // preset.id is the only identity that changes the fetched command.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset.id]);

  const onSave = () => {
    setBusy(true);
    setSaveErr(null);
    setSaved(false);
    (async () => {
      try {
        await patchPreset(preset.id, { cmdOverride: draft });
        setSaved(true);
        onReload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : "save failed");
      } finally {
        setBusy(false);
      }
    })();
  };

  const mono = "12px/1.5 var(--font-mono, monospace)";
  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <button
          className="ae-b"
          onClick={onBack}
          style={{
            font: "600 10px var(--font-sans)",
            letterSpacing: ".09em",
            textTransform: "uppercase",
            padding: "5px 10px",
            borderRadius: "var(--r-xs)",
            cursor: "pointer",
            color: "var(--fg1)",
            background: "transparent",
            border: "1px solid var(--line-strong)",
          }}
        >
          Back
        </button>
        <span style={{ font: "var(--t-label)", color: "var(--fg2)" }}>Full view · admin command</span>
      </div>

      <div
        style={{
          display: "flex",
          gap: 9,
          padding: "10px 11px",
          marginBottom: 12,
          borderRadius: "var(--r-xs)",
          background: "var(--blue-tint)",
          border: "1px solid var(--line)",
        }}
      >
        <Icon name="lock" size={14} color="var(--blue-400)" />
        <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>
          This is the ffmpeg command the engine runs, as an argv array (no shell). Edit the arguments
          and Save; the tokens {"{{input}}"}, {"{{output}}"}, {"{{width}}"}, {"{{height}}"} are substituted per rung
          at encode time. Leave it empty to use the managed EVE build.
        </span>
      </div>

      {loadErr ? (
        <div style={{ font: "var(--t-body-sm)", color: "var(--danger, #d66)", marginBottom: 12 }}>
          Could not load the generated command: {loadErr}
        </div>
      ) : !cmd ? (
        <div style={{ font: "var(--t-body-sm)", color: "var(--fg4)", marginBottom: 12 }}>Loading command…</div>
      ) : (
        <>
          {/* Read-only: the exact generated command per rung, the real starting point. */}
          <div style={{ font: "var(--t-label)", color: "var(--fg3)", marginBottom: 6 }}>Generated command (per rung)</div>
          {cmd.rungs.map((r) => (
            <div key={r.rung} style={{ marginBottom: 8 }}>
              <div style={{ font: "var(--t-label)", color: "var(--fg2)", marginBottom: 3 }}>{r.rung}</div>
              <pre
                style={{
                  margin: 0,
                  padding: "8px 9px",
                  font: mono,
                  color: "var(--fg2)",
                  background: "var(--surface-2, rgba(0,0,0,.25))",
                  border: "1px solid var(--line)",
                  borderRadius: "var(--r-xs)",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {commandText(r.argv)}
              </pre>
            </div>
          ))}
        </>
      )}

      <div style={{ font: "var(--t-label)", color: "var(--fg3)", margin: "12px 0 6px" }}>Command override (argv, no shell)</div>
      <textarea
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setSaved(false);
        }}
        spellCheck={false}
        rows={6}
        placeholder="-i {{input}} … {{output}}   (empty = managed EVE build)"
        style={{
          width: "100%",
          boxSizing: "border-box",
          font: mono,
          color: "var(--fg1)",
          background: "var(--surface-2, rgba(0,0,0,.25))",
          border: "1px solid var(--line-strong)",
          borderRadius: "var(--r-xs)",
          padding: "8px 9px",
          resize: "vertical",
        }}
      />

      {saveErr && (
        <div style={{ font: "var(--t-body-sm)", color: "var(--danger, #d66)", marginTop: 8 }}>{saveErr}</div>
      )}
      {saved && !saveErr && (
        <div style={{ font: "var(--t-body-sm)", color: "var(--fg3)", marginTop: 8 }}>Saved.</div>
      )}

      <button
        className="ae-b"
        onClick={onSave}
        disabled={busy}
        style={{
          marginTop: 12,
          width: "100%",
          minHeight: 32,
          font: "600 10px var(--font-sans)",
          letterSpacing: ".09em",
          textTransform: "uppercase",
          borderRadius: "var(--r-xs)",
          cursor: busy ? "default" : "pointer",
          color: "var(--fg1)",
          background: "transparent",
          border: "1px solid var(--line-strong)",
          opacity: busy ? 0.6 : 1,
        }}
      >
        {busy ? "Saving…" : "Save command"}
      </button>
    </>
  );
}

// EveRestrictedProfile is the ONLY editor surface for an EVE profile: four
// selectors (delivery/packaging, codec, include-audio, ABR ladder resolution),
// everything else managed by EVE and hidden. Non-owners see just this; owners
// additionally get a "Full view" entry point (Phase 3) wired via onOpenFull.
function EveRestrictedProfile({
  preset,
  isOwner,
  onOpenFull,
}: {
  preset: Preset;
  isOwner: boolean;
  onOpenFull: () => void;
}) {
  // Delivery = the preset's delivery formats (or its primary container).
  const delivery = (preset.formats && preset.formats.length ? preset.formats : [preset.container])
    .map((f) => f.toUpperCase())
    .join(" · ");
  const audio = preset.audioDisabled ? "Off" : "On";
  // ABR ladder shows resolution only, limited to the EVE rung set (any stray
  // rung outside the set is dropped from the display rather than shown).
  const rungs = [...(preset.ladder ?? [])]
    .filter((r) => EVE_RUNG_SET.includes(r.name))
    .sort((a, b) => b.height - a.height)
    .map((r) => r.name);
  const ladder = rungs.length ? rungs.join(" · ") : EM_DASH;

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 9,
          padding: "10px 11px",
          marginBottom: 12,
          borderRadius: "var(--r-xs)",
          background: "var(--blue-tint)",
          border: "1px solid var(--line)",
        }}
      >
        <Icon name="lock" size={14} color="var(--blue-400)" />
        <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>
          EVE manages every other setting. You choose delivery, codec, audio, and the ladder.
        </span>
      </div>
      {/* The four (and only four) EVE selectors. They are managed profiles, so
          the rows read the seeded values today; the interactive selector wiring
          rides in with the EVE adapter. */}
      <Row label="Delivery / packaging" value={delivery || EM_DASH} />
      <Row label="Codec" value={codecLabel(preset.videoCodec)} />
      <Row label="Include audio" value={audio} />
      <Row label="ABR ladder" value={ladder} />
      {isOwner && (
        <button
          className="ae-b"
          onClick={onOpenFull}
          title="Open the full EVE profile view (workspace owners only)"
          style={{
            marginTop: 12,
            width: "100%",
            minHeight: 32,
            font: "600 10px var(--font-sans)",
            letterSpacing: ".09em",
            textTransform: "uppercase",
            borderRadius: "var(--r-xs)",
            cursor: "pointer",
            color: "var(--fg1)",
            background: "transparent",
            border: "1px solid var(--line-strong)",
          }}
        >
          Full view
        </button>
      )}
    </>
  );
}

export function OutputProfilePanel({
  preset,
  eve,
  job,
  isOwner,
  canEdit,
  onEdit,
  onReload,
}: {
  preset: Preset | null;
  eve: EveState;
  job: Job | null;
  isOwner: boolean;
  // canEdit: ownership-based (owner any; a user only their own custom; EVE/standard
  // owner-only). Shows the Edit affordance; the server enforces it regardless.
  canEdit: boolean;
  onEdit: () => void;
  onReload: () => void;
}) {
  void job; // job duration would feed est. output; no FT-3 probe route yet.

  // The EVE "Full view" (owners only) is an INLINE surface toggled here, not the
  // manual PresetEditorModal. It opens the admin editable-command panel.
  const [showFull, setShowFull] = useState(false);

  // An EVE profile (eve=true) is edited through the restricted four-selector
  // surface, never the full manual list. The EVE toggle filters the picker so
  // eve.on implies an EVE preset, but we key off preset.eve directly so the
  // panel is correct even if the two ever diverge.
  const isEvePreset = Boolean(preset?.eve);

  if (isEvePreset && preset) {
    return (
      <Panel
        title="EVE profile"
        style={{ flex: 1, minHeight: 0 }}
        bodyStyle={{ overflowY: "auto", flex: 1 }}
        right={<span style={{ font: "var(--t-label)", color: "#fff", whiteSpace: "nowrap" }}>{preset.name}</span>}
      >
        {/* Phase 3: owners get the inline admin editable-command "Full view";
            non-owners see only the restricted four-selector surface. The full
            view NEVER opens the manual PresetEditorModal. */}
        {isOwner && showFull ? (
          <EveFullView preset={preset} onBack={() => setShowFull(false)} onReload={onReload} />
        ) : (
          <EveRestrictedProfile preset={preset} isOwner={isOwner} onOpenFull={() => setShowFull(true)} />
        )}
      </Panel>
    );
  }

  const editBtn =
    !eve.on && preset && canEdit ? (
      <button
        className="ae-b"
        onClick={onEdit}
        title="Edit this profile"
        style={{
          font: "600 10px var(--font-sans)",
          letterSpacing: ".09em",
          textTransform: "uppercase",
          padding: "5px 10px",
          borderRadius: "var(--r-xs)",
          cursor: "pointer",
          color: "var(--fg1)",
          background: "transparent",
          border: "1px solid var(--line-strong)",
        }}
      >
        Edit
      </button>
    ) : (
      <span style={{ font: "var(--t-label)", color: "#fff", whiteSpace: "nowrap" }}>{eve.on ? "EVE · per title" : EM_DASH}</span>
    );

  return (
    <Panel title="Output profile" style={{ flex: 1, minHeight: 0, opacity: eve.on ? 0.72 : 1 }} bodyStyle={{ overflowY: "auto", flex: 1 }} right={editBtn}>
      {eve.on ? (
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 11px", marginBottom: 12, borderRadius: "var(--r-xs)", background: "var(--blue-tint)", border: "1px solid var(--line)" }}>
          <Icon name="lock" size={14} color="var(--blue-400)" />
          <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>Codec, ladder and rate control set by EVE</span>
        </div>
      ) : !preset ? (
        <div style={{ font: "var(--t-body-sm)", color: "var(--fg4)", marginBottom: 12 }}>No profile selected.</div>
      ) : !canEdit ? (
        <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "10px 11px", marginBottom: 12, borderRadius: "var(--r-xs)", background: "var(--blue-tint)", border: "1px solid var(--line)" }}>
          <Icon name="lock" size={14} color="var(--blue-400)" />
          <span style={{ font: "var(--t-body-sm)", color: "var(--fg2)" }}>Settings for this profile are managed by the workspace owner. You can select and apply it; its settings are not shown for your role.</span>
        </div>
      ) : null}

      {/* The settings rows read the preset's full fields (ladder, formats, rate
          control). The server returns those only when the viewer may see them
          (owner on any; creator on their own custom), so they render only when
          eve.on (safe label strings), no preset (em dash), or canEdit. For a
          label-only preset the notice above stands in and the rows are hidden,
          which both prevents a null-field crash and enforces the owner-only
          settings rule. */}
      <Row label="Name" value={eve.on ? "EVE · per title" : preset ? preset.name : EM_DASH} />
      {eve.on || !preset || canEdit ? (
        <>
          <Row label="Formats" value={eve.on ? eve.formats.join(" · ") || "none" : preset ? formatsSummary(preset) : EM_DASH} />
          <Row label="Codec" value={eve.on ? "EVE per title" : preset ? codecSummary(preset) : EM_DASH} />
          <Row label="Rate control" value={eve.on ? "Per title" : preset ? rateSummary(preset) : EM_DASH} />
          <Row label="ABR ladder" value={eve.on ? "Per-title ladder" : preset ? ladderSummary(preset) : EM_DASH} />
          <Row label="Resolution" value={eve.on ? "Per-title ladder" : preset ? resolutionLabel(preset) : EM_DASH} />
          <Row label="Keyframe" value={eve.on ? "Scene-aligned" : preset ? keyframeSummary(preset) : EM_DASH} />
          <Row label="Segment" value={eve.on ? "Scene-aligned" : preset ? segmentSummary(preset) : EM_DASH} />
          <Row label="Scene cut" value={eve.on ? "Scene-aware" : preset ? ((preset.sceneCut ?? true) ? "On" : "Off") : EM_DASH} />
          <Row label="Picture" value={eve.on ? "Per title" : preset ? pictureSummary(preset) : EM_DASH} />
          <Row label="Encoder speed" value={eve.on ? EM_DASH : preset ? speedLabel(preset) : EM_DASH} />
          <Row label="Frame rate" value={eve.on ? "Source cadence" : preset ? (preset.perRungFrameRate ? "Per rung" : preset.frameRate ? `${preset.frameRate} fps` : "Source") : EM_DASH} />
          <Row label="Audio" value={eve.on ? "Loudness normalised" : preset ? audioSummary(preset) : EM_DASH} />
          {/* Est. output needs the source probe duration (R3); no probe route yet, so em dash. */}
          <Row label="Est. output" value={eve.on ? EM_DASH : preset ? estOutputLabel(preset, null) : EM_DASH} />
        </>
      ) : null}
    </Panel>
  );
}
